import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import ButtonBS from '../../../components/common/ButtonBS'
import PageLayout from '../../../components/common/PageLayout/PageLayout4'
import useTranslations from '../../../hooks/useTranslations'
import translations from '../../../middlewares/i18n/CoolingOffScreen.json'
import { cancelOperation } from '../../../actions/authActions'
import '../../../styles/CoolingOffScreen/CoolingOffScreen.css'

// available_at is ISO-8601 UTC but may arrive without a Z/offset suffix
const parseUTC = (timestamp) => {
  if (!timestamp) return null
  const hasOffset = /(Z|[+-]\d{2}:?\d{2})$/i.test(timestamp)
  const date = new Date(hasOffset ? timestamp : `${timestamp}Z`)
  return isNaN(date.getTime()) ? null : date
}

const getRemainingMs = (target) => {
  if (!target) return 0
  return Math.max(0, target.getTime() - Date.now())
}

const CoolingOffScreen = () => {
  const i18n = useTranslations(translations)
  const dispatch = useDispatch()

  const title = useSelector(state => state.view.title)
  const content = useSelector(state => state.view.content)
  const availableAt = useSelector(state => state.view.available_at)

  const target = useMemo(() => parseUTC(availableAt), [availableAt])
  const [remaining, setRemaining] = useState(() => getRemainingMs(target))

  const expired = remaining <= 0

  useEffect(() => {
    setRemaining(getRemainingMs(target))
  }, [target])

  useEffect(() => {
    if (!target || expired) return
    const interval = setInterval(() => {
      setRemaining(getRemainingMs(target))
    }, 1000)
    return () => clearInterval(interval)
  }, [target, expired])

  const exit = useCallback(
    () => {
      dispatch(cancelOperation())
    }, []
  )

  const totalSeconds = Math.floor(remaining / 1000)
  const days = Math.floor(totalSeconds / 86400)
  const segments = [
    { label: i18n.hours, value: Math.floor((totalSeconds % 86400) / 3600) },
    { label: i18n.minutes, value: Math.floor((totalSeconds % 3600) / 60) },
    { label: i18n.seconds, value: totalSeconds % 60 }
  ]
  if(days > 0)
    segments.unshift({ label: i18n.days, value: days })

  return(
    <PageLayout
        title={title || i18n.title}
        price
        power
        big
    >
      <div className='cooling-off-container'>
        <div className='warning-icon cooling-off-icon' />
        { expired ? (
          <>
            <p className='cooling-off-content'>{i18n.expiredMessage}</p>
            <ButtonBS
              className='cooling-off-button'
              content={i18n.expiredButton}
              onClick={ exit }
            />
          </>
        ) : (
          <>
            <p className='cooling-off-content'>{content}</p>
            <p className='cooling-off-available-in'>{i18n.availableIn}</p>
            <div className='cooling-off-countdown'>
              {segments.map((segment) => (
                <div className='cooling-off-segment' key={segment.label}>
                  <span className='cooling-off-segment-value'>{String(segment.value).padStart(2, '0')}</span>
                  <span className='cooling-off-segment-label'>{segment.label}</span>
                </div>
              ))}
            </div>
            <ButtonBS
              className='cooling-off-button'
              content={i18n.cancel}
              onClick={ exit }
            />
          </>
        )}
      </div>
    </PageLayout>
  )
}

export default CoolingOffScreen
